import { html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { BaseElement } from '../base/base-element.js';

export type TriggerType = 'button' | 'hamburger' | 'icon';
export type MenuPosition = 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';

/**
 * A dropdown menu component that supports button, hamburger, and icon triggers.
 *
 * @slot trigger - Custom trigger element (overrides trigger-type)
 * @slot - Default slot for menu items
 *
 * @fires menu-open - Fired when the menu opens
 * @fires menu-close - Fired when the menu closes
 * @fires menu-item-click - Fired when a menu item is clicked (detail: { item: HTMLElement })
 */
@customElement('dropdown-menu')
export class DropdownMenu extends BaseElement {
    static styles = [
        BaseElement.styles,
        css`
            :host {
                display: inline-block;
                position: relative;
            }

            :host([fixed]) {
                position: fixed;
            }

            .trigger {
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 0.5rem;
                border-radius: 0.375rem;
                color: var(--text-primary, #f1f5f9);
                transition: background-color 0.2s;
            }

            .trigger:hover {
                background-color: var(--surface-light, #334155);
            }

            .trigger.active {
                background-color: var(--surface-light, #334155);
            }

            /* Hamburger trigger - matches admin hamburger button style */
            :host([trigger-type="hamburger"]) .trigger {
                flex-direction: column;
                gap: 5px;
                width: 44px;
                min-width: 44px;
                max-width: 44px;
                height: 38px;
                padding: 0;
                background-color: var(--surface-light, #334155);
                border: 2px solid var(--border, #475569);
                border-radius: 10px;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
                transition: all 0.2s ease;
            }

            :host([trigger-type="hamburger"]) .trigger:hover {
                background-color: var(--primary-color, #3b82f6);
                border-color: var(--primary-color, #3b82f6);
                transform: translateY(-2px);
            }

            /* Hamburger icon */
            .hamburger-icon {
                display: flex;
                flex-direction: column;
                gap: 5px;
                width: 18px;
            }

            .hamburger-icon span {
                display: block;
                height: 2px;
                width: 100%;
                background: var(--text-primary, #f1f5f9);
                border-radius: 1px;
                transition: background 0.2s, transform 0.2s, opacity 0.2s;
            }

            :host([trigger-type="hamburger"]) .trigger:hover .hamburger-icon span {
                background: white;
            }

            :host([open]) .hamburger-icon span:nth-child(1) {
                transform: translateY(7px) rotate(45deg);
            }

            :host([open]) .hamburger-icon span:nth-child(2) {
                opacity: 0;
            }

            :host([open]) .hamburger-icon span:nth-child(3) {
                transform: translateY(-7px) rotate(-45deg);
            }

            /* Button trigger */
            .trigger-button {
                padding: 0.5rem 1rem;
                border: 1px solid var(--border, #475569);
                border-radius: 0.375rem;
                background-color: var(--surface, #1e293b);
                font-size: 0.875rem;
                font-weight: 500;
            }

            /* Menu panel */
            .menu {
                position: absolute;
                z-index: 99;
                background-color: var(--surface, #1e293b);
                border: 2px solid var(--border, #475569);
                border-radius: 0.5rem;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                min-width: var(--dropdown-min-width, 250px);
                padding: var(--dropdown-padding, 0.5rem);
                display: none;
            }

            :host([open]) .menu {
                display: block;
            }

            /* Position variants */
            .menu.bottom-left {
                top: 100%;
                left: 0;
                margin-top: 0.25rem;
            }

            .menu.bottom-right {
                top: 100%;
                right: 0;
                margin-top: 0.25rem;
            }

            .menu.top-left {
                bottom: 100%;
                left: 0;
                margin-bottom: 0.25rem;
            }

            .menu.top-right {
                bottom: 100%;
                right: 0;
                margin-bottom: 0.25rem;
            }

            /* Default slotted menu item styles.
               !important on padding/margin to override global * { padding: 0; margin: 0; }
               reset, which takes precedence over ::slotted() in the cascade. */
            ::slotted(button),
            ::slotted([role="menuitem"]) {
                display: block;
                width: 100%;
                padding: 0.75rem 1rem !important;
                margin: 0 !important;
                text-align: left;
                background: transparent;
                border: none;
                cursor: pointer;
                border-radius: 0.375rem;
                transition: background-color 0.2s;
                color: var(--text-primary, #f1f5f9);
                font-size: 0.875rem;
                font-weight: 500;
            }

            ::slotted(button:hover),
            ::slotted([role="menuitem"]:hover) {
                background-color: var(--surface-light, #334155);
            }
        `,
    ];

    @property({ type: String, attribute: 'trigger-type', reflect: true })
    triggerType: TriggerType = 'button';

    @property({ type: String, reflect: true })
    position: MenuPosition = 'bottom-left';

    @property({ type: String })
    label = '';

    @property({ type: String })
    icon = '';

    @property({ type: Boolean, reflect: true })
    open = false;

    @property({ type: Boolean, reflect: true })
    fixed = false;

    private _boundCloseOnOutsideClick = this._closeOnOutsideClick.bind(this);

    connectedCallback(): void {
        super.connectedCallback();
        document.addEventListener('click', this._boundCloseOnOutsideClick);
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        document.removeEventListener('click', this._boundCloseOnOutsideClick);
    }

    private _closeOnOutsideClick(e: Event): void {
        if (!this.open) return;
        const path = e.composedPath();
        if (!path.includes(this)) {
            this._close();
        }
    }

    private _toggle(e: Event): void {
        e.stopPropagation();
        if (this.open) {
            this._close();
        } else {
            this._open();
        }
    }

    private _open(): void {
        this.open = true;
        this.dispatchEvent(new CustomEvent('menu-open', { bubbles: true, composed: true }));
    }

    private _close(): void {
        this.open = false;
        this.dispatchEvent(new CustomEvent('menu-close', { bubbles: true, composed: true }));
    }

    private _onMenuClick(e: Event): void {
        const target = e.target as HTMLElement;
        // Only fire for slotted items (buttons or elements with role="menuitem")
        if (target && (target.tagName === 'BUTTON' || target.getAttribute('role') === 'menuitem')) {
            this.dispatchEvent(
                new CustomEvent('menu-item-click', {
                    bubbles: true,
                    composed: true,
                    detail: { item: target },
                })
            );
            this._close();
        }
    }

    private _renderTrigger() {
        switch (this.triggerType) {
            case 'hamburger':
                return html`
                    <button
                        class="trigger ${this.open ? 'active' : ''}"
                        @click=${this._toggle}
                        aria-expanded=${this.open}
                        aria-haspopup="menu"
                        aria-label=${this.label || 'Menu'}
                    >
                        <div class="hamburger-icon">
                            <span></span>
                            <span></span>
                            <span></span>
                        </div>
                    </button>
                `;
            case 'icon':
                return html`
                    <button
                        class="trigger ${this.open ? 'active' : ''}"
                        @click=${this._toggle}
                        aria-expanded=${this.open}
                        aria-haspopup="menu"
                        aria-label=${this.label || 'Menu'}
                    >
                        ${this.icon || '...'}
                    </button>
                `;
            case 'button':
            default:
                return html`
                    <button
                        class="trigger trigger-button ${this.open ? 'active' : ''}"
                        @click=${this._toggle}
                        aria-expanded=${this.open}
                        aria-haspopup="menu"
                    >
                        ${this.label || 'Menu'}
                    </button>
                `;
        }
    }

    render() {
        const hasTriggerSlot = this.querySelector('[slot="trigger"]') !== null;

        return html`
            ${hasTriggerSlot
                ? html`<slot name="trigger" @click=${this._toggle}></slot>`
                : this._renderTrigger()}
            <div
                class="menu ${this.position}"
                role="menu"
                @click=${this._onMenuClick}
            >
                <slot></slot>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'dropdown-menu': DropdownMenu;
    }
}
